import { readChatSessionIdentity } from 'src/auth/sessionEvents';
// useChatEventRouter (issue #418) — single hook that consumes the chat
// Socket.IO singleton from `useChatSocket()` and routes every Sprint 3
// event (#414 vocabulary) into the React Query cache plus the toast
// stack.
//
// Why a single router:
//   - The Sprint 4 surfaces (Messenger 2.0 tabs, job-detail panel,
//     broadcast composer, support inbox) all watch the same query keys.
//     Without a central router each surface would attach its own
//     `socket.on('chat:message:new', ...)` listener and they'd race each
//     other on cache writes.
//   - Cleanup is symmetric: every `.on(...)` registers a matching
//     `.off(...)` in the effect's cleanup. No leaks across navigation.
//
// Mount: once at the Messenger route level, next to `ChatSocketProvider`
// from #400 — never app-wide, so login screens don't hold a socket open.

import { useEffect } from 'react';
import { InfiniteData, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';

import { useChatSocket } from './ChatSocketProvider';
import { chatKeys } from 'src/queries/chat/keys';

import type {
  ChatErrorEvent,
  ChatMessage,
  ChatMessageNewEvent,
  ChatMessagesPage,
  ChatReceiptUpdateEvent,
  JobBroadcastNewEvent,
  SupportStatusUpdateEvent,
  SupportThreadNewEvent,
} from 'src/models/chat';

interface ChatEventRouterOptions {
  /**
   * Caller's role name (from `localStorage.PLS.user.Role.name`). Required
   * to gate the support-inbox listener — non-admin sessions must not
   * register a `support:thread:new` listener because the inbox query
   * returns 403 anyway.
   */
  role?: string;
}

const ADMIN_ROLES = new Set(['ADMIN', 'SUPER_ADMIN', 'LABOR_COORDINATOR']);

export function useChatEventRouter(options: ChatEventRouterOptions = {}): void {
  const { socket } = useChatSocket();
  const queryClient = useQueryClient();
  const role = options.role;

  useEffect(() => {
    if (!socket) return undefined;
    const sessionIdentity = readChatSessionIdentity();

    // -----------------------------------------------------------------
    // chat:message:new → append into the chatId message stream + nudge
    // the inbox row's `lastMessage` preview.
    // -----------------------------------------------------------------
    const handleMessageNew = (event: ChatMessageNewEvent) => {
      if (readChatSessionIdentity() !== sessionIdentity) return;
      const chatId = event?.chatId;
      const message = event?.message;
      if (typeof chatId !== 'number' || !message) return;
      const key = chatKeys.chatMessages(chatId);
      queryClient.setQueryData<InfiniteData<ChatMessagesPage>>(key, (old) => {
        if (!old) return old;
        const [first, ...rest] = old.pages;
        if (!first) return old;
        // Skip duplicates: an optimistic message will share the same
        // server id once the round-trip lands; tolerate the case where
        // the same socket emit arrives twice.
        if (first.data.some((m) => m.id === message.id)) return old;
        const nextFirst: ChatMessagesPage = {
          ...first,
          data: [message, ...first.data],
          meta: { ...first.meta, count: first.meta.count + 1 },
        };
        return { ...old, pages: [nextFirst, ...rest] };
      });
      queryClient.invalidateQueries(chatKeys.myChatsRoot());
    };

    // -----------------------------------------------------------------
    // chat:receipt:update → patch the affected message in-place so the
    // read-avatar stack at the bottom of the bubble updates.
    // -----------------------------------------------------------------
    const handleReceiptUpdate = (event: ChatReceiptUpdateEvent) => {
      if (readChatSessionIdentity() !== sessionIdentity) return;
      const { chatId, messageId, userId, readAt } = event ?? {};
      if (
        typeof chatId !== 'number' ||
        typeof messageId !== 'number' ||
        typeof userId !== 'number'
      ) {
        return;
      }
      const key = chatKeys.chatMessages(chatId);
      queryClient.setQueryData<InfiniteData<ChatMessagesPage>>(key, (old) => {
        if (!old) return old;
        return {
          ...old,
          pages: old.pages.map((page) => ({
            ...page,
            data: page.data.map((m) =>
              m.id === messageId
                ? mergeReceipt(m, { userId, readAt })
                : m
            ),
          })),
        };
      });
    };

    // -----------------------------------------------------------------
    // job:broadcast:new → invalidate the broadcasts tab and the inbox.
    // -----------------------------------------------------------------
    const handleBroadcastNew = (event: JobBroadcastNewEvent) => {
      if (readChatSessionIdentity() !== sessionIdentity) return;
      if (!event?.chatId) return;
      queryClient.invalidateQueries(
        chatKeys.myChats({ type: 'JOB_BROADCAST' })
      );
      queryClient.invalidateQueries(chatKeys.myChatsRoot());
      if (event.jobId) {
        queryClient.invalidateQueries(chatKeys.jobChats(event.jobId));
      }
    };

    // -----------------------------------------------------------------
    // support:thread:new → admin/coordinator only. Refresh the inbox and
    // toast so the next ticket is visible without a page reload.
    // -----------------------------------------------------------------
    const handleSupportThreadNew = (event: SupportThreadNewEvent) => {
      if (readChatSessionIdentity() !== sessionIdentity) return;
      if (!event?.chatId) return;
      queryClient.invalidateQueries(chatKeys.supportInboxRoot());
      const subject = event.subject || 'New support thread';
      toast.info(`New support thread: ${subject}`, {
        position: 'top-right',
        autoClose: 7000,
      });
    };

    // -----------------------------------------------------------------
    // support:status:update → refresh the inbox row + thread metadata.
    // -----------------------------------------------------------------
    const handleSupportStatusUpdate = (
      event: SupportStatusUpdateEvent & { chatId?: number }
    ) => {
      if (readChatSessionIdentity() !== sessionIdentity) return;
      const chatId = event?.chatId;
      if (typeof chatId !== 'number') return;
      queryClient.invalidateQueries(chatKeys.supportInboxRoot());
      queryClient.invalidateQueries(chatKeys.chat(chatId));
    };

    // -----------------------------------------------------------------
    // chat:error → surface the gateway's failure reason as a toast.
    // -----------------------------------------------------------------
    const handleChatError = (event: ChatErrorEvent) => {
      if (readChatSessionIdentity() !== sessionIdentity) return;
      if (!event) return;
      const message =
        typeof event.message === 'string'
          ? event.message
          : 'Chat action failed';
      const tag = event.event ? `[${event.event}] ` : '';
      toast.error(`${tag}${message}`, {
        position: 'top-right',
        autoClose: 5000,
      });
    };

    socket.on('chat:message:new', handleMessageNew);
    socket.on('chat:receipt:update', handleReceiptUpdate);
    socket.on('job:broadcast:new', handleBroadcastNew);
    socket.on('support:status:update', handleSupportStatusUpdate);
    socket.on('chat:error', handleChatError);

    const registerSupport = role && ADMIN_ROLES.has(role);
    if (registerSupport) {
      socket.on('support:thread:new', handleSupportThreadNew);
    }

    return () => {
      socket.off('chat:message:new', handleMessageNew);
      socket.off('chat:receipt:update', handleReceiptUpdate);
      socket.off('job:broadcast:new', handleBroadcastNew);
      socket.off('support:status:update', handleSupportStatusUpdate);
      socket.off('chat:error', handleChatError);
      if (registerSupport) {
        socket.off('support:thread:new', handleSupportThreadNew);
      }
    };
  }, [socket, queryClient, role]);
}

function mergeReceipt(
  message: ChatMessage,
  receipt: { userId: number; readAt: string }
): ChatMessage {
  const existing = message.receipts ?? [];
  const without = existing.filter((r) => r.userId !== receipt.userId);
  return { ...message, receipts: [...without, receipt] };
}
